import instance from "@store/middleware/api";
import { isViewingAsCustomer } from "@utils/viewAs";

type MediaType = "track" | "album";
type MediaEvent = "play" | "cart_add" | "heart" | "listen";

/**
 * Fire-and-forget engagement ping for a track/album. `view` (detail-page load)
 * and `download` are recorded server-side where they happen; `play`, `cart_add`,
 * `heart`, and `listen` are reported from here. `cart_add` + `heart` feed both
 * the catalog Trending sort and the cart-funnel analytics; `listen` carries the
 * listened duration (seconds) for the Playtime analytics. Failures are swallowed
 * — analytics must never interrupt the UX.
 */
export const recordMediaEvent = (mediaId: string | undefined, type: MediaType, event: MediaEvent, seconds?: number): void => {
  if (!mediaId) return;
  if (event === "listen" && !(seconds && seconds > 0)) return;
  const body: Record<string, unknown> = { mediaId, type, event };
  if (event === "listen") body.seconds = Math.round(seconds as number);
  instance.post("/media-info/event", body).catch(() => {});
};

/**
 * Listen ping that survives the page closing — uses `navigator.sendBeacon` so the
 * final in-progress listen isn't lost when the user closes the tab mid-play.
 * Falls back to the normal axios ping if sendBeacon is unavailable.
 */
export const recordListenBeacon = (mediaId: string | undefined, seconds: number, type: MediaType = "track"): void => {
  if (!mediaId || !(seconds > 0)) return;
  const payload = { mediaId, type, event: "listen", seconds: Math.round(seconds) };
  try {
    const url = `${process.env.REACT_APP_API_URL}/media-info/event`;
    if (isViewingAsCustomer()) return;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: "application/json" }));
      return;
    }
  } catch {
    /* fall through to axios */
  }
  recordMediaEvent(mediaId, type, "listen", seconds);
};
